import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/components/ui/card";
import { Button } from "@shared/components/ui/button";
import { Badge } from "@shared/components/ui/badge";
import { Switch } from "@shared/components/ui/switch";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";
import {
  AlertTriangle,
  Bell,
  BellOff,
  Settings,
  Eye,
  EyeOff,
  RefreshCw,
  Package,
  TrendingDown,
  AlertCircle,
  CheckCircle,
  Clock,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { getLowStockProductsWithMovements } from '@features/products/services/productService';
import { toast } from "sonner";
import { TIME_INTERVALS } from "@shared/utils/constants";

export function StockAlerts() {
  const { t } = useTranslation();
  const [alertsEnabled, setAlertsEnabled] = useState(true);
  const [alertThreshold, setAlertThreshold] = useState(10);
  const [criticalThreshold, setCriticalThreshold] = useState(5);
  const [showOutOfStock, setShowOutOfStock] = useState(true);
  const [showLowStock, setShowLowStock] = useState(true);
  const [showCriticalStock, setShowCriticalStock] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30);

  // Fetch low stock products
  const { data: lowStockData, isLoading, refetch } = useQuery({
    queryKey: ["lowStockAlerts", alertThreshold],
    queryFn: () => getLowStockProductsWithMovements(alertThreshold),
    refetchInterval: alertsEnabled ? refreshInterval * 1000 : false,
    enabled: alertsEnabled,
  });

  // Check for critical alerts
  useEffect(() => {
    if (lowStockData?.data && alertsEnabled) {
      const criticalProducts = lowStockData.data.filter(p => p.stock <= criticalThreshold);
      const outOfStockProducts = lowStockData.data.filter(p => p.stock === 0);
      
      if (outOfStockProducts.length > 0) {
        toast.error(
          t("products.criticalAlertOutOfStock", { count: outOfStockProducts.length }),
          { duration: 10000 }
        );
      } else if (criticalProducts.length > 0) {
        toast.warning(
          t("products.criticalAlertLowStock", { count: criticalProducts.length }),
          { duration: 8000 }
        );
      }
    }
  }, [lowStockData, alertsEnabled, criticalThreshold, t]);

  const getAlertLevel = (stock) => {
    if (stock === 0) return "out-of-stock";
    if (stock <= criticalThreshold) return "critical";
    if (stock <= alertThreshold) return "low";
    return "normal";
  };

  const getAlertIcon = (level) => {
    switch (level) {
      case "out-of-stock":
        return <AlertTriangle className="w-5 h-5 text-red-600" />;
      case "critical":
        return <AlertCircle className="w-5 h-5 text-orange-600" />;
      case "low":
        return <TrendingDown className="w-5 h-5 text-yellow-600" />;
      default:
        return <CheckCircle className="w-5 h-5 text-green-600" />;
    }
  };

  const getAlertColor = (level) => {
    switch (level) {
      case "out-of-stock":
        return "border-red-200 bg-red-50";
      case "critical":
        return "border-orange-200 bg-orange-50";
      case "low":
        return "border-yellow-200 bg-yellow-50";
      default:
        return "border-green-200 bg-green-50";
    }
  };

  const getAlertBadgeColor = (level) => {
    switch (level) {
      case "out-of-stock":
        return "bg-red-100 text-red-800";
      case "critical":
        return "bg-orange-100 text-orange-800";
      case "low":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-green-100 text-green-800";
    }
  };

  const filteredProducts = lowStockData?.data?.filter(product => {
    const level = getAlertLevel(product.stock);
    
    if (level === "out-of-stock" && !showOutOfStock) return false;
    if (level === "critical" && !showCriticalStock) return false;
    if (level === "low" && !showLowStock) return false;
    
    return true;
  }) || [];

  const alertStats = {
    outOfStock: lowStockData?.data?.filter(p => p.stock === 0).length || 0,
    critical: lowStockData?.data?.filter(p => p.stock > 0 && p.stock <= criticalThreshold).length || 0,
    low: lowStockData?.data?.filter(p => p.stock > criticalThreshold && p.stock <= alertThreshold).length || 0,
  };

  return (
    <div className="space-y-6">
      {/* Alert Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            {t("products.alertSettings")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="alerts-enabled" className="text-sm font-medium">
                  {t("products.enableAlerts")}
                </Label>
                <Switch
                  id="alerts-enabled"
                  checked={alertsEnabled}
                  onCheckedChange={setAlertsEnabled}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {alertsEnabled ? t("products.alertsEnabled") : t("products.alertsDisabled")}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="alert-threshold" className="text-sm font-medium">
                {t("products.alertThreshold")}
              </Label>
              <Input
                id="alert-threshold"
                type="number"
                min="0"
                value={alertThreshold}
                onChange={(e) => setAlertThreshold(parseInt(e.target.value) || 0)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="critical-threshold" className="text-sm font-medium">
                {t("products.criticalThreshold")}
              </Label>
              <Input
                id="critical-threshold"
                type="number"
                min="0"
                value={criticalThreshold}
                onChange={(e) => setCriticalThreshold(parseInt(e.target.value) || 0)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="refresh-interval" className="text-sm font-medium">
                {t("products.refreshInterval")} (s)
              </Label>
              <Select value={refreshInterval.toString()} onValueChange={(value) => setRefreshInterval(parseInt(value))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIME_INTERVALS.REFRESH_INTERVALS.map((interval) => (
                    <SelectItem key={interval.value} value={interval.value.toString()}>
                      {interval.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Alert Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            {t("products.alertFilters")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-2">
              <Switch
                id="show-out-of-stock"
                checked={showOutOfStock}
                onCheckedChange={setShowOutOfStock}
              />
              <Label htmlFor="show-out-of-stock" className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                {t("products.showOutOfStock")}
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="show-critical-stock"
                checked={showCriticalStock}
                onCheckedChange={setShowCriticalStock}
              />
              <Label htmlFor="show-critical-stock" className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-orange-600" />
                {t("products.showCriticalStock")}
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="show-low-stock"
                checked={showLowStock}
                onCheckedChange={setShowLowStock}
              />
              <Label htmlFor="show-low-stock" className="flex items-center gap-2">
                <TrendingDown className="w-4 h-4 text-yellow-600" />
                {t("products.showLowStock")}
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Alert Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("products.totalAlerts")}
            </CardTitle>
            <Bell className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {filteredProducts.length}
            </div>
            <p className="text-xs text-muted-foreground">
              {t("products.activeAlerts")}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("products.outOfStock")}
            </CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {alertStats.outOfStock}
            </div>
            <p className="text-xs text-muted-foreground">
              {t("products.requiresImmediate")}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("products.criticalStock")}
            </CardTitle>
            <AlertCircle className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {alertStats.critical}
            </div>
            <p className="text-xs text-muted-foreground">
              ≤ {criticalThreshold} {t("products.units")}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("products.lowStock")}
            </CardTitle>
            <TrendingDown className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              {alertStats.low}
            </div>
            <p className="text-xs text-muted-foreground">
              ≤ {alertThreshold} {t("products.units")}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Alert List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              {alertsEnabled ? <Bell className="w-5 h-5 text-green-600" /> : <BellOff className="w-5 h-5 text-gray-600" />}
              {t("products.stockAlerts")}
            </CardTitle>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="w-4 h-4 mr-2" />
              {t("common.refresh")}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {Array(5).fill(0).map((_, i) => (
                <div key={i} className="h-16 bg-muted animate-pulse rounded" />
              ))}
            </div>
          ) : filteredProducts.length > 0 ? (
            <div className="space-y-3">
              {filteredProducts.map((product) => {
                const alertLevel = getAlertLevel(product.stock);
                return (
                  <div
                    key={product._id}
                    className={`p-4 border rounded-lg ${getAlertColor(alertLevel)}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {getAlertIcon(alertLevel)}
                        <div>
                          <div className="font-medium">{product.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {product.user?.name} • ID: {product._id.slice(-6)}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className="font-bold text-lg">
                            {product.stock}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {t("products.units")}
                          </div>
                        </div>
                        
                        <Badge variant="secondary" className={getAlertBadgeColor(alertLevel)}>
                          {t(`products.alertLevels.${alertLevel}`)}
                        </Badge>
                      </div>
                    </div>
                    
                    {product.lastMovement && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          {t("products.lastMovement")}: {product.lastMovement.adjustment > 0 ? "+" : ""}
                          {product.lastMovement.adjustment} {t("products.units")} • 
                          {new Date(product.lastMovement.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">
                {t("products.noAlerts")}
              </h3>
              <p className="text-muted-foreground">
                {t("products.allProductsAboveThreshold", { threshold: alertThreshold })}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
} 